import {PopoverPrimitive as Popover} from "ch3-ui-lib";
import {Search} from "lucide-react";
import {type ReactElement, useMemo, useState} from "react";
import {FlagImage} from "react-international-phone";
import {getCallingCountries} from "../Dialer/services/countryDataService";

export interface PickedCountry {
    alpha2: string;
    name: string | undefined;
    countryCallingCodes: string[];
}

interface CountryPickerProps {
    /** The flag button (or any element) that opens the list. */
    trigger: ReactElement;
    /** ISO code of the chosen country, marked in the list. */
    value?: string;
    onSelect: (country: PickedCountry) => void;
}

/** Country list for phone number fields: the dark DS menu with a search field on top. */
export const CountryPicker = ({trigger, value, onSelect}: CountryPickerProps) => {
    const [open, setOpen] = useState(false);
    const [phrase, setPhrase] = useState('');
    const all = useMemo(() => getCallingCountries().filter((c) => c.name).sort((a, b) => a.name!.localeCompare(b.name!)), []);
    const query = phrase.trim().toLowerCase().replace(/^\+/, '');
    const countries = query
        ? all.filter((c) => c.name?.toLowerCase().includes(query) || c.countryCallingCodes[0]?.replace('+', '').includes(query))
        : all;

    return (
        <Popover.Root open={open} onOpenChange={(next) => { setOpen(next); if (!next) setPhrase(''); }}>
            <Popover.Trigger asChild>{trigger}</Popover.Trigger>
            <Popover.Portal>
                <Popover.Content align="start" sideOffset={6} collisionPadding={8}
                                 className="z-[70] flex w-[280px] flex-col rounded-xl bg-zinc-950 p-1 text-white shadow-menu outline-none">
                    <label className="flex h-9 items-center gap-2 rounded-chip bg-zinc-800 px-2.5">
                        <Search size={14} aria-hidden="true" className="shrink-0 text-zinc-400"/>
                        <input
                            autoFocus
                            value={phrase}
                            onChange={(e) => setPhrase(e.target.value)}
                            placeholder="Search country or code"
                            aria-label="Search country or code"
                            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-callout text-white shadow-none outline-none ring-0 placeholder:text-zinc-500 focus:outline-none focus:ring-0"
                        />
                    </label>
                    <div role="listbox" aria-label="Countries" className="mt-1 max-h-[320px] overflow-y-auto overflow-x-hidden">
                        {countries.length === 0 && <div className="px-2.5 py-2 text-footnote text-zinc-400">No countries found</div>}
                        {countries.map((c) => {
                            const selected = c.alpha2 === value?.toUpperCase();
                            return (
                                <button
                                    key={c.alpha2}
                                    type="button"
                                    role="option"
                                    aria-selected={selected}
                                    onClick={() => {
                                        onSelect(c);
                                        setOpen(false);
                                        setPhrase('');
                                    }}
                                    className={`box-border flex min-h-9 w-full cursor-pointer items-center gap-2.5 rounded-chip border-0 bg-transparent px-2.5 text-left text-callout text-white outline-none hover:bg-zinc-800 focus-visible:bg-zinc-800 ${selected ? 'font-semibold' : 'font-medium'}`}
                                >
                                    <FlagImage iso2={c.alpha2.toLowerCase()} size={18}/>
                                    <span className="min-w-0 flex-1 truncate">{c.name}</span>
                                    <span className="text-footnote text-zinc-400">{c.countryCallingCodes[0]}</span>
                                </button>
                            );
                        })}
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};
